import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { useSelector } from "react-redux";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import {
    errorMessage,
    formatAmount,
    formatDate,
    formatStatus,
    formatUsername,
    permissionCssClass,
    ucFirst,
    getOrderBalanceAfter
} from "../../../util/Helpers";
import DynamicSimpleTable from "../../../util/DynamicSimpleReactTable";
import cogoToast from "cogo-toast";
import { Permissions, PermissionType } from "../../../util/Permissions";
import {
    useGetOrdersAggregateQuery,
    useSubmitOrdersHistoryExportMutation
} from "../../../../redux/apiSlice";
import CancelOrderModal
    from "./modals/CancelOrderModal";
import ViewOrderModal
    from "./modals/ViewOrderModal";
import EditOrderModal from "./modals/EditOrderModal";
import { FilterGroup } from "../../Common/components/FilterGroup";
import { useQuery } from "../../../util/Hooks";
import { Permission } from "../../Common/layout/Permission"
import { PARTNERS } from "../../../util/constants/Partners";
import { SubHeader } from "../../Common/layout/SubHeader";
import { ContentRoute } from "../../../../_metronic/layout";
import { Redirect, Switch } from "react-router-dom";

const asNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === "number") return value;
    if (typeof value === "string") {
        const v = value.trim();
        if (v === "") return 0;
        const numeric = Number(v);
        if (!Number.isNaN(numeric)) return numeric;
        const cleaned = v.replace(/,/g, "");
        const parsed = Number(cleaned);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

const toStartOfDayZulu = (dateValue) => {
    if (!dateValue) return "";
    const v = `${dateValue}`.trim();
    if (v === "") return "";
    if (v.includes("T")) return v;
    return `${v}T00:00:00.00Z`;
};

const toEndOfDayZulu = (dateValue) => {
    if (!dateValue) return "";
    const v = `${dateValue}`.trim();
    if (v === "") return "";
    if (v.includes("T")) return v;
    return `${v}T23:59:59.999Z`;
};

const getExportErrorMessage = (error) => (
    error?.data
    || error?.error
    || error?.message
    || errorMessage(error)
    || "Orders export failed"
);

const OrdersAggregateSummary = ({ userGroup, filters, statusOverride }) => {
    const provider = filters?.provider || "";
    const liveData = (filters?.type ?? "live") === "live";
    const startDate = toStartOfDayZulu(filters?.startDate);
    const endDate = toEndOfDayZulu(filters?.endDate);
    const status = statusOverride ?? filters?.status ?? "";
    const userId = filters?.userId || "";

    const shouldSkip = !provider || !startDate;
    const { data, isFetching, error } = useGetOrdersAggregateQuery(
        { userGroup, liveData, provider, startDate, endDate, status, userId },
        { skip: shouldSkip }
    );

    useEffect(() => {
        if (error) cogoToast.error(errorMessage(error));
    }, [error]);

    const aggregate = useMemo(() => {
        const payload = data?.data ?? data ?? null;
        if (!payload || typeof payload !== "object") return null;
        return payload;
    }, [data]);

    const currency = useMemo(() => (
        aggregate?.BUY?.currency
        || aggregate?.SELL?.currency
        || aggregate?.SELL_SHORT?.currency
        || aggregate?.BUY_TO_COVER?.currency
        || "NGN"
    ), [aggregate]);

    const entries = useMemo(() => ([
        { key: "BUY", label: "BUY" },
        { key: "SELL", label: "SELL" },
        { key: "SELL_SHORT", label: "SELL SHORT" },
        { key: "BUY_TO_COVER", label: "BUY TO COVER" },
    ]), []);

    return (
        <div className="mb-6">
            {shouldSkip ? (
                <div className="p-3 rounded-sm bg-light-secondary text-muted">
                    Select a provider and start date to view order aggregates.
                </div>
            ) : (
                <div className="d-flex flex-wrap">
                    {entries.map(({ key, label }) => {
                        const total = asNumber(aggregate?.[key]?.total);
                        const cardCurrency = aggregate?.[key]?.currency || currency;
                        return (
                            <div key={key} className="mr-4 mb-4" style={{ minWidth: 220, flex: "1 1 220px" }}>
                                <div className="p-4 rounded-sm bg-light-primary">
                                    <div className="text-muted">{label}</div>
                                    <div className="font-weight-bolder font-size-h4">
                                        {isFetching ? "Loading..." : formatAmount(cardCurrency, total)}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};


export const OrderManagement = (
    {
        match: {
            params: { userGroup },
        },
    }
) => {
    const { PUBLIC_URL } = process.env;
    const adminUser = useSelector(state => state.auth.user);

    const query = useQuery()
    const { type } = JSON.parse(query.get('filters') ?? "{\"type\":\"live\"}");
    

    const [submitOrdersHistoryExport] = useSubmitOrdersHistoryExportMutation();
    const [isExporting, setIsExporting] = useState(false);

    // Orders Redux state
    const tableRef = useRef();

    /* Order Modal State Management */
    const [orderModal, setOrderModal] = useState({
        showViewOrderModal: false,
        showEditOrderModal: false,
        showCancelOrderModal: false,
        details: null
    });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setOrderModal({ ...orderModal, [modal]: !orderModal[modal] })
    }

    /* View Order Details */
    const viewOrder = details => {
        setOrderModal({
            ...orderModal,
            showViewOrderModal: true,
            details
        })
    }

    /* Edit Order Modal*/
    const editOrder = details => {
        setOrderModal({
            ...orderModal,
            showEditOrderModal: true,
            details
        })
    }

    /* Cancel Order Modal */
    const showCancelOrderModal = (details) => {
        setOrderModal({ ...orderModal, showCancelOrderModal: true, details });
    };

    const buildOrdersExportPayload = ({
        filters = {},
        statusOverride = "",
        format = "csv",
    } = {}) => {
        const providerValue = filters?.provider || "";

        const payload = {
            userGroup,
            providers: providerValue,
            provider: providerValue,
            startDate: filters?.startDate || "",
            endDate: filters?.endDate || "",
            status: statusOverride || filters?.status || "",
            userId: filters?.userId || "",
            liveData: filters?.type === "live",
            format,
        };

        return Object.entries(payload).reduce((accumulator, [key, value]) => {
            if (value === undefined || value === null || `${value}`.trim() === "") {
                return accumulator;
            }

            accumulator[key] = value;
            return accumulator;
        }, {});
    };

    const handleOrdersExport = async ({ filters, statusOverride = "", format = "csv" }) => {
        try {
            setIsExporting(true);
            cogoToast.info(`Submitting ${format.toUpperCase()} orders export...`);

            await submitOrdersHistoryExport(
                buildOrdersExportPayload({ filters, statusOverride, format })
            ).unwrap();
            cogoToast.success(`Orders ${format.toUpperCase()} report will be sent to your email.`);
        } catch (error) {
            cogoToast.error(getExportErrorMessage(error));
        } finally {
            setIsExporting(false);
        }
    };

    const renderOrdersExportActions = (filters, statusOverride = "") => (
        <Permission permission={Permissions.REPORTS} permissionType={PermissionType.CREATE}>
            <CardHeaderToolbar>
                {/*
                <button
                    type="button"
                    className="btn btn-primary mr-4"
                    disabled={isExporting}
                    onClick={() => downloadOrdersReport({
                        userGroup,
                        liveData: filters?.type === "live",
                        provider: filters?.provider || "",
                        startDate: filters?.startDate || "",
                        endDate: filters?.endDate || "",
                        status: statusOverride || filters?.status || "",
                        userId: filters?.userId || "",
                        format: "xlsx",
                    })}
                >
                    Export XLSX
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isExporting}
                    onClick={() => downloadOrdersReport({
                        userGroup,
                        liveData: filters?.type === "live",
                        provider: filters?.provider || "",
                        startDate: filters?.startDate || "",
                        endDate: filters?.endDate || "",
                        status: statusOverride || filters?.status || "",
                        userId: filters?.userId || "",
                        format: "csv",
                    })}
                >
                    Export CSV
                </button>
                */}
                <button
                    type="button"
                    className="btn btn-primary"
                    disabled={isExporting}
                    onClick={() => handleOrdersExport({ filters, statusOverride, format: "csv" })}
                >
                    {isExporting ? "Submitting..." : "Export Orders"}
                </button>
            </CardHeaderToolbar>
        </Permission>
    );

    const formatActivity = row => {
        return <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
            <span className={`svg-icon svg-icon-lg svg-icon-${row.side === "BUY" ? "primary" : "danger"} mr-3`}>
                <SVG
                    title=""
                    src={toAbsoluteUrl("/media/svg/icons/Shopping/Cart2.svg")}
                />
            </span>
            <div>
                <a href={`${PUBLIC_URL}/instruments/${row?.instrumentId}`} rel="noopener noreferrer"
                    target="_blank" className="text-dark-75 font-weight-bolder text-hover-primary mb-1 font-size-lg">
                    {row.symbol}
                    <span className="text-muted pl-2"> - {row.side}</span>
                </a>
            </div>
        </div>
    }

    /* Action Buttons */
    const orderAction = (row) => {
        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <Button
                    title="View Order"
                    className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                    onClick={_ => viewOrder(row)}
                >
                    <span className="svg-icon svg-icon-md svg-icon-primary">
                        <SVG
                            title="View Order"
                            src={toAbsoluteUrl(
                                "/media/svg/icons/General/Eye-2.svg"
                            )}
                        />
                    </span>
                </Button>

                <Button
                    title="Edit Order"
                    className={`btn btn-icon btn-light btn-hover-primary btn-sm mx-3 ${permissionCssClass(adminUser, Permissions.ORDERS, PermissionType.UPDATE)}`}
                    onClick={_ => editOrder(row)}
                    disabled={(row.status !== "PENDING" && row.status !== "PROCESSING" && row.status !== "CANCELLING" && row.status !== "MANUAL_PROCESSING")}
                >
                    <span className={`svg-icon svg-icon-md svg-icon-${(row.status !== "PENDING" && row.status !== "PROCESSING" && row.status !== "CANCELLING" && row.status !== "MANUAL_PROCESSING" ) ? "muted" : "primary"}`}>
                        <SVG
                            title="Edit Order"
                            src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")}
                        />
                    </span>
                </Button>

                <button
                    disabled={(row.status !== "PENDING" && row.status !== "PROCESSING")}
                    className={`btn btn-icon btn-white btn-hover-danger btn-sm ${permissionCssClass(adminUser, Permissions.ORDERS, PermissionType.DELETE)}`}
                    onClick={_ => showCancelOrderModal(row)}
                >
                    <span
                        className={`svg-icon svg-icon-md svg-icon-${(row.status !== "PENDING" && row.status !== "PROCESSING") ? "muted" : "danger"}`}>
                        <SVG
                            title="Cancel Order"
                            src={toAbsoluteUrl("/media/svg/icons/Code/Error-circle.svg")}
                        />
                    </span>
                </button>
            </span>
        );
    };
    
    return <>
        <SubHeader>
            <SubHeader.Nav>
                <SubHeader.NavItem label={"All Orders"} to={`/orders/${userGroup}/all`} />
                <SubHeader.NavItem label={"On Hold Orders"} to={`/orders/${userGroup}/on-hold`} />
                <SubHeader.NavItem label={"Manual Processing Orders"} to={`/orders/${userGroup}/manual-processing`} />
                <SubHeader.NavItem label={"Aggregate"} to={`/orders/${userGroup}/aggregate`} />
            </SubHeader.Nav>
        </SubHeader>

        <div className="py-4 px-6">
            <Switch>
                <Redirect exact={true} from="/orders/:userGroup" to="/orders/:userGroup/all" />
                <ContentRoute path="/orders/:userGroup/aggregate" permission={Permissions.ORDERS}>
                    <FilterGroup
                        key={`${userGroup}-aggregate`}
                        defaultFilters={{ provider: "infoware_ucml", startDate: new Date().toISOString().split("T")[0] }}
                    >
                        <FilterGroup.Date name={"startDate"} label={"Start Date"} className={"mb-4"} />
                        <div className="d-flex">
                            <div className={"mb-4 mr-4"}>
                                <FilterGroup.Filter className={"mr-4"} name={"provider"} label={"Provider"}>
                                    <FilterGroup.Option value={""}>Select</FilterGroup.Option>
                                    {PARTNERS.map(({ name, value }) => (
                                        <FilterGroup.Option key={value} value={value}>{name}</FilterGroup.Option>
                                    ))}
                                </FilterGroup.Filter>
                            </div>
                        </div>
                        <FilterGroup.Content>
                            {({ filters }) => (
                                <Card>
                                    <CardHeader title="Orders Aggregate" />
                                    <CardBody>
                                        <OrdersAggregateSummary userGroup={userGroup} filters={filters} />
                                    </CardBody>
                                </Card>
                            )}
                        </FilterGroup.Content>
                    </FilterGroup>
                </ContentRoute>
                <ContentRoute path="/orders/:userGroup/all" permission={Permissions.ORDERS}>
                    <FilterGroup
                        key={userGroup}
                        defaultFilters={{ "type": "live", endDate: new Date().toISOString().split('T')[0] }}
                    >
                        <FilterGroup.DateRange className={"mb-4"} />
                        <div className="d-flex">
                            <div className={"mb-4 mr-4"}>
                                <FilterGroup.Filter className={"mr-4"} name={"type"} label={"Type"}>
                                    <FilterGroup.Option value={"live"}>Live</FilterGroup.Option>
                                    <FilterGroup.Option value={"demo"}>Demo</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            <div className={"mr-4"}>
                                {
                                    type === "demo" ?
                                        <FilterGroup.Filter className={"mr-4"} name={"provider"} label={"Provider"}>
                                            <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                            <FilterGroup.Option value={"paper_ngn"}>Paper NGN</FilterGroup.Option>
                                            <FilterGroup.Option value={"paper_usd"}>Paper USD</FilterGroup.Option>
                                        </FilterGroup.Filter>
                                        :
                                        <FilterGroup.Filter className={"mr-4"} name={"provider"} label={"Provider"}>
                                            <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                            {
                                                PARTNERS.map(({ name, value }) => (
                                                    <FilterGroup.Option value={value}>{name}</FilterGroup.Option>
                                                ))
                                            }
                                        </FilterGroup.Filter>
                                }
                            </div>
                            <div className={"mr-4"}>
                                <FilterGroup.Filter name={"limit"} label={"Limit"}>
                                    <FilterGroup.Option value="">10</FilterGroup.Option>
                                    <FilterGroup.Option value="20">20</FilterGroup.Option>
                                    <FilterGroup.Option value="50">50</FilterGroup.Option>
                                    <FilterGroup.Option value="100">100</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            <div className={"mr-4"}>
                                <FilterGroup.Filter name={"status"} label={"Status"}>
                                    <FilterGroup.Option value="">All</FilterGroup.Option>
                                    <FilterGroup.Option value="PENDING">PENDING</FilterGroup.Option>
                                    <FilterGroup.Option value="PARTIAL_FILL">PARTIAL_FILL</FilterGroup.Option>
                                    <FilterGroup.Option value="ON_HOLD">ON HOLD</FilterGroup.Option>
                                    <FilterGroup.Option value="MANUAL_PROCESSING">MANUAL PROCESSING</FilterGroup.Option>
                                    <FilterGroup.Option value="CANCELLED">CANCELLED</FilterGroup.Option>
                                    <FilterGroup.Option value="REJECTED">REJECTED</FilterGroup.Option>
                                    <FilterGroup.Option value="CANCELLING">CANCELLING</FilterGroup.Option>
                                    <FilterGroup.Option value="PROCESSING">PROCESSING</FilterGroup.Option>
                                    <FilterGroup.Option value="COMPLETED">COMPLETED</FilterGroup.Option>
                                    <FilterGroup.Option value="FAILED">FAILED</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                        </div>
                        <FilterGroup.Content>
                            {
                                ({ filters }) => {
                                    return <Card>
                                        <CardHeader title={`${ucFirst(filters?.type)} orders list`}>
                                            {renderOrdersExportActions(filters)}
                                        </CardHeader>
                                        <CardBody>
                                            <DynamicSimpleTable
                                                key={userGroup}
                                                queryParams={
                                                    `userGroup=${userGroup}`
                                                    + `&limit=${filters?.limit || 10}`
                                                    + `&liveData=${filters?.type === "live"}`
                                                    + `&provider=${filters?.provider || ""}`
                                                    + `&startDate=${filters?.startDate || ""}`
                                                    + `&endDate=${filters?.endDate || ""}`
                                                    + `&status=${filters?.status || ""}`
                                                    + `&userId=${filters?.userId || ""}`
                                                }
                                                searchBox={true}
                                                source={{ url: `/orders` }}
                                                columns={[
                                                    { title: "ACTIVITY", format: formatActivity },
                                                    { title: "ID", key: "id" },
                                                    { title: "REFERENCE", key: "refid" },
                                                    { title: "USER", format: row => formatUsername(row.userId, row.username, null, row.accountId, "orders") },
                                                    { title: "CHN", key: "chnNo", format: row => row.chnNo || "NA" },
                                                    { title: "CSCS", key: "cscsNo", format: row => row.cscsNo || "NA" },
                                                    { title: "PRICE", key: "price" },
                                                    { title: "QUANTITY", key: "quantity" },
                                                    { title: "PROVIDER", key: "provider" },
                                                    { title: "EXCHANGE", key: "exchange" },
                                                    { title: "TYPE", key: "type" },
                                                    { title: "AMOUNT", format: row => formatAmount(row.currency, row.amount) },
                                                    { title: "FEE", format: row => formatAmount(row.currency, Math.abs(row.total - row.amount)) },
                                                    { title: "TOTAL", format: row => formatAmount(row.currency, row.total) },
                                                    { title: "STATUS", format: row => formatStatus(row.status) },
                                                    { title: "BALANCE BEFORE", format: row => formatAmount(row.currency, row.accountBalance) },
                                                    { title: "BALANCE AFTER", format: row => formatAmount(row.currency, getOrderBalanceAfter(row)) },
                                                    { title: "CREATED ON", format: (row) => formatDate(row.created) },
                                                    { title: "", format: orderAction },
                                                ]}
                                                ref={tableRef}
                                                onError={(err) =>
                                                    cogoToast.error(errorMessage(err))
                                                }
                                                refreshIcon={true}
                                            />
                                        </CardBody>
                                    </Card>
                                }
                            }
                        </FilterGroup.Content>

                    </FilterGroup>
                </ContentRoute>
                <ContentRoute path="/orders/:userGroup/on-hold" permission={Permissions.ORDERS}>
                    <FilterGroup
                        key={userGroup}
                        defaultFilters={{ "type": "live", endDate: new Date().toISOString().split('T')[0] }}
                    >
                        <FilterGroup.DateRange className={"mb-4"} />
                        <div className="d-flex">
                            <div className={"mb-4 mr-4"}>
                                <FilterGroup.Filter className={"mr-4"} name={"type"} label={"Type"}>
                                    <FilterGroup.Option value={"live"}>Live</FilterGroup.Option>
                                    <FilterGroup.Option value={"demo"}>Demo</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            <div className={"mr-4"}>
                                {
                                    type === "demo" ?
                                        <FilterGroup.Filter className={"mr-4"} name={"provider"} label={"Provider"}>
                                            <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                            <FilterGroup.Option value={"paper_ngn"}>Paper NGN</FilterGroup.Option>
                                            <FilterGroup.Option value={"paper_usd"}>Paper USD</FilterGroup.Option>
                                        </FilterGroup.Filter>
                                        :
                                        <FilterGroup.Filter className={"mr-4"} name={"provider"} label={"Provider"}>
                                            <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                            {
                                                PARTNERS.map(({ name, value }) => (
                                                    <FilterGroup.Option value={value}>{name}</FilterGroup.Option>
                                                ))
                                            }
                                        </FilterGroup.Filter>
                                }
                            </div>
                            <div className={"mr-4"}>
                                <FilterGroup.Filter name={"limit"} label={"Limit"}>
                                    <FilterGroup.Option value="">10</FilterGroup.Option>
                                    <FilterGroup.Option value="20">20</FilterGroup.Option>
                                    <FilterGroup.Option value="50">50</FilterGroup.Option>
                                    <FilterGroup.Option value="100">100</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            
                        </div>
                        <FilterGroup.Content>
                            {
                                ({ filters }) => {
                                    return <Card>
                                        <CardHeader title={`${ucFirst(filters?.type)} orders list`}>
                                            {renderOrdersExportActions(filters, "ON_HOLD")}
                                        </CardHeader>
                                        <CardBody>
                                            <DynamicSimpleTable
                                                key={userGroup}
                                                queryParams={
                                                    `userGroup=${userGroup}`
                                                    + `&limit=${filters?.limit || 10}`
                                                    + `&liveData=${filters?.type === "live"}`
                                                    + `&provider=${filters?.provider || ""}`
                                                    + `&startDate=${filters?.startDate || ""}`
                                                    + `&endDate=${filters?.endDate || ""}`
                                                    + `&status=${filters?.status || "ON_HOLD"}`
                                                    + `&userId=${filters?.userId || ""}`
                                                }
                                                searchBox={true}
                                                source={{ url: `/orders` }}
                                                columns={[
                                                    { title: "ACTIVITY", format: formatActivity },
                                                    { title: "ID", key: "id" },
                                                    { title: "REFERENCE", key: "refid" },
                                                    { title: "USER", format: row => formatUsername(row.userId, row.username, null, row.accountId, "orders") },
                                                    { title: "CHN", key: "chnNo", format: row => row.chnNo || "NA" },
                                                    { title: "CSCS", key: "cscsNo", format: row => row.cscsNo || "NA" },
                                                    { title: "PRICE", key: "price" },
                                                    { title: "QUANTITY", key: "quantity" },
                                                    { title: "PROVIDER", key: "provider" },
                                                    { title: "EXCHANGE", key: "exchange" },
                                                    { title: "TYPE", key: "type" },
                                                    { title: "AMOUNT", format: row => formatAmount(row.currency, row.amount) },
                                                    { title: "FEE", format: row => formatAmount(row.currency, Math.abs(row.total - row.amount)) },
                                                    { title: "TOTAL", format: row => formatAmount(row.currency, row.total) },
                                                    { title: "STATUS", format: row => formatStatus(row.status) },
                                                    { title: "BALANCE BEFORE", format: row => formatAmount(row.currency, row.accountBalance) },
                                                    { title: "BALANCE AFTER", format: row => formatAmount(row.currency, getOrderBalanceAfter(row)) },
                                                    { title: "CREATED ON", format: (row) => formatDate(row.created) },
                                                    { title: "", format: orderAction },
                                                ]}
                                                ref={tableRef}
                                                onError={(err) =>
                                                    cogoToast.error(errorMessage(err))
                                                }
                                                refreshIcon={true}
                                            />
                                        </CardBody>
                                    </Card>
                                }
                            }
                        </FilterGroup.Content>

                    </FilterGroup>
                </ContentRoute>
                <ContentRoute path="/orders/:userGroup/manual-processing" permission={Permissions.ORDERS}>
                    <FilterGroup
                        key={userGroup}
                        defaultFilters={{ "type": "live", endDate: new Date().toISOString().split('T')[0] }}
                    >
                        <FilterGroup.DateRange className={"mb-4"} />
                        <div className="d-flex">
                            <div className={"mb-4 mr-4"}>
                                <FilterGroup.Filter className={"mr-4"} name={"type"} label={"Type"}>
                                    <FilterGroup.Option value={"live"}>Live</FilterGroup.Option>
                                    <FilterGroup.Option value={"demo"}>Demo</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            <div className={"mr-4"}>
                                {
                                    type === "demo" ?
                                        <FilterGroup.Filter className={"mr-4"} name={"provider"} label={"Provider"}>
                                            <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                            <FilterGroup.Option value={"paper_ngn"}>Paper NGN</FilterGroup.Option>
                                            <FilterGroup.Option value={"paper_usd"}>Paper USD</FilterGroup.Option>
                                        </FilterGroup.Filter>
                                        :
                                        <FilterGroup.Filter className={"mr-4"} name={"provider"} label={"Provider"}>
                                            <FilterGroup.Option value={""}>All</FilterGroup.Option>
                                            {
                                                PARTNERS.map(({ name, value }) => (
                                                    <FilterGroup.Option value={value}>{name}</FilterGroup.Option>
                                                ))
                                            }
                                        </FilterGroup.Filter>
                                }
                            </div>
                            <div className={"mr-4"}>
                                <FilterGroup.Filter name={"limit"} label={"Limit"}>
                                    <FilterGroup.Option value="">10</FilterGroup.Option>
                                    <FilterGroup.Option value="20">20</FilterGroup.Option>
                                    <FilterGroup.Option value="50">50</FilterGroup.Option>
                                    <FilterGroup.Option value="100">100</FilterGroup.Option>
                                </FilterGroup.Filter>
                            </div>
                            
                        </div>
                        <FilterGroup.Content>
                            {
                                ({ filters }) => {
                                    return <Card>
                                        <CardHeader title={`${ucFirst(filters?.type)} orders list`}>
                                            {renderOrdersExportActions(filters, "MANUAL_PROCESSING")}
                                        </CardHeader>
                                        <CardBody>
                                            <DynamicSimpleTable
                                                key={userGroup}
                                                queryParams={
                                                    `userGroup=${userGroup}`
                                                    + `&limit=${filters?.limit || 10}`
                                                    + `&liveData=${filters?.type === "live"}`
                                                    + `&provider=${filters?.provider || ""}`
                                                    + `&startDate=${filters?.startDate || ""}`
                                                    + `&endDate=${filters?.endDate || ""}`
                                                    + `&status=${filters?.status || "MANUAL_PROCESSING"}`
                                                    + `&userId=${filters?.userId || ""}`
                                                }
                                                searchBox={true}
                                                source={{ url: `/orders` }}
                                                columns={[
                                                    { title: "ACTIVITY", format: formatActivity },
                                                    { title: "ID", key: "id" },
                                                    { title: "REFERENCE", key: "refid" },
                                                    { title: "USER", format: row => formatUsername(row.userId, row.username, null, row.accountId, "orders") },
                                                    { title: "CHN", key: "chnNo", format: row => row.chnNo || "NA" },
                                                    { title: "CSCS", key: "cscsNo", format: row => row.cscsNo || "NA" },
                                                    { title: "PRICE", key: "price" },
                                                    { title: "QUANTITY", key: "quantity" },
                                                    { title: "PROVIDER", key: "provider" },
                                                    { title: "EXCHANGE", key: "exchange" },
                                                    { title: "TYPE", key: "type" },
                                                    { title: "AMOUNT", format: row => formatAmount(row.currency, row.amount) },
                                                    { title: "FEE", format: row => formatAmount(row.currency, Math.abs(row.total - row.amount)) },
                                                    { title: "TOTAL", format: row => formatAmount(row.currency, row.total) },
                                                    { title: "STATUS", format: row => formatStatus(row.status) },
                                                    { title: "BALANCE BEFORE", format: row => formatAmount(row.currency, row.accountBalance) },
                                                    { title: "BALANCE AFTER", format: row => formatAmount(row.currency, getOrderBalanceAfter(row)) },
                                                    { title: "CREATED ON", format: (row) => formatDate(row.created) },
                                                    { title: "", format: orderAction },
                                                ]}
                                                ref={tableRef}
                                                onError={(err) =>
                                                    cogoToast.error(errorMessage(err))
                                                }
                                                refreshIcon={true}
                                            />
                                        </CardBody>
                                    </Card>
                                }
                            }
                        </FilterGroup.Content>

                    </FilterGroup>
                </ContentRoute>
            </Switch>
        </div>



        <ViewOrderModal
            {...orderModal}
            onHide={toggleModal("showViewOrderModal")}
            tableRef={tableRef}
        />
        {
            orderModal.showEditOrderModal &&
            <EditOrderModal
                {...orderModal}
                onHide={toggleModal("showEditOrderModal")}
                tableRef={tableRef}
            />
        }
        {
            orderModal.showCancelOrderModal &&
            <CancelOrderModal
                {...orderModal}
                onHide={toggleModal("showCancelOrderModal")}
                provider={orderModal.details?.provider}
                tableRef={tableRef}
            />
        }




    </>

};
